/**
 * WebSmDebrief — after reporting in. The report itself is filed on the
 * laptop (the live mission's report window, report-window.tsx), as a Glow of
 * Type "SecretMission"; this step is what follows the vault run. On their
 * first mission they earn their key and engrave their name on it first
 * (KeyEngraving); then the agent says well done and asks when the next
 * mission should come (WellDone). "Now" goes straight back to the Doors;
 * Tomorrow / Schedule
 * emit CLICK1 → the sign-off, which books the text (at the picked time, for
 * Schedule).
 *
 * Options: KeyTitle / KeyMessage / KeyButton / WellDoneMessage /
 *   WellDoneButton / DeskImage / BgColor / AccentColor.
 */
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { SENDS_TEXTS } from "@/shared/lib/send-texts";
import { useEffect, useState, type CSSProperties } from "react";
import { useFunnelStore } from "@/store";
import { useStepperStore } from "@/store/stepper";
import { useQueryClient } from "@tanstack/react-query";
import { LoginModal } from "@/features/funnel/components/login-modal";
import { useHasPhone } from "@/features/funnel/hooks/use-phone-login";
import { useMissionFile } from "@/features/funnel/api/use-mission-file";
import { ACTION_BTN_CLASS, stepVars } from "./step-shell";
import type { StepTypeProps } from "./types";
import noHandlerDesk from "./no-handler-desk.jpg";
import { KeyEngraving } from "./key-engraving";
import { WellDone, type NextMission } from "./well-done";
import { useHandlerCompanionEnabled } from "./companion-config";
import { MissionGraduation } from "./mission-graduation";
import { DEV_KEY_EVENT, filedCount, takeDevShowKey } from "../dev-mission";
import { comebackLoopEnabled } from "./comeback-config";
import { COMPANION_ADVENTURES } from "./companion-config";
import { SealTomorrow } from "./seal-tomorrow";
import { DoorView } from "./vault-run";
import { LOCK_WORDS } from "./vault-clips";
import { tomorrowMorning } from "./seal-time";
import { engravedName, useMemberName } from "./operative-name";

export default function WebSmDebriefType({ getOpt, dispatch }: StepTypeProps) {
  const companionEnabled = useHandlerCompanionEnabled();
  const adventureId = useStepperStore((s) => s.adventureId);
  const bg = getOpt("BgColor") || "#0a0809";
  const accent = getOpt("AccentColor") || "#d6a94e";
  const desk = getOpt("DeskImage") || noHandlerDesk;
  const keyTitle = getOpt("KeyTitle") || "You have earned your key";
  const keyMessage = getOpt("KeyMessage") || "Engrave your name on it.";
  const keyButton = getOpt("KeyButton") || "Engrave it";

  // On their first mission they engrave the key they earned first; then the
  // agent says well done and asks when the next one should come. (The dev
  // menu's Key engraving shows the key whatever the mission.)
  const [devKey, setDevKey] = useState(takeDevShowKey);
  // kept on the run: a reload after engraving must not burn the name again
  const engraved = useFunnelStore((s) => s.keyEngraved);
  const setEngraved = useFunnelStore((s) => s.setKeyEngraved);
  // the lock the vault opened, fixed when they submitted (absent when they
  // reported in elsewhere, e.g. another device)
  const storedNo = useFunnelStore((s) => s.filedMissionNo);
  const handler = useFunnelStore((s) => s.handler);
  const setNextMissionAt = useFunnelStore((s) => s.setNextMissionAt);
  // the Comeback Loop: instead of a plain confirmation, tomorrow's case is
  // sealed with the door they choose now
  const sealing = comebackLoopEnabled();
  const sealTomorrow = useFunnelStore((s) => s.sealTomorrow);
  const { data: memberName } = useMemberName();
  const operative = engravedName() || memberName || "Operative";
  // their door, with its locks as they stand, for a moment before the agent
  // says well done: the picture that makes someone want the next one
  const [doorShown, setDoorShown] = useState(false);
  const goTo = useStepperStore((s) => s.goTo);
  const qc = useQueryClient();
  const { data: hasPhone } = useHasPhone();
  // the choice waiting on their number (Tomorrow / a scheduled time)
  const [textAsk, setTextAsk] = useState<NextMission | null>(null);
  // the choice they are confirming: Confirm goes on to HQ, Change goes back
  const [confirming, setConfirming] = useState<NextMission | null>(null);
  // signed in just now (asked once, even before the session says so)
  const [verified, setVerified] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookingError, setBookingError] = useState("");
  // "Now" skips the sign-off (and so its booked text) for the next door
  const moveOn = (choice: NextMission) =>
    choice === "now"
      ? goTo(companionEnabled && missionNo >= 3 ? "Handlers" : "Doors")
      : dispatch("CLICK1");
  // (filing refetches the file before this screen opens, so it counts the
  // mission just filed)
  const {
    data: filedMissions = [],
    isSuccess: fileLoaded,
    isError: fileFailed,
  } = useMissionFile();
  // the mission just filed (1 = their first): the submit's own count when
  // we have it, else their file's
  const missionNo = storedNo ?? Math.max(1, filedCount(filedMissions));
  const known = storedNo !== null || fileLoaded;
  // This step mounts after the vault's unlock animation completes. Mission
  // three goes directly to promotion; never mount the scheduling choices.
  const promote = companionEnabled && known && missionNo === 3;
  useEffect(() => {
    if (!promote) return;
    setNextMissionAt(null);
    goTo("Handlers");
  }, [promote, goTo, setNextMissionAt]);
  // the key only on a first mission we know of: a file that could not be read
  // goes straight to well done rather than offering it again
  const showKey = (devKey || (known && missionNo === 1)) && !engraved;
  // dev menu's Key engraving, picked while already on this screen
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const show = () => {
      takeDevShowKey();
      setDevKey(true);
      setEngraved(false);
    };
    window.addEventListener(DEV_KEY_EVENT, show);
    return () => window.removeEventListener(DEV_KEY_EVENT, show);
  }, [setEngraved]);

  // Escape on the confirmation is Change
  useEffect(() => {
    if (confirming === null || booking) return;
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && setConfirming(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirming, booking]);

  return (
    <section
      style={
        {
          ...stepVars({ accent, bg }),
          "--sm-bg": bg,
        } as CSSProperties
      }
      className="relative h-[100dvh] overflow-clip bg-[var(--sm-bg)] font-serif"
    >
      <img
        src={desk}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[50%_100%]"
      />

      {/* not before their file is in: it says which mission this was */}
      {promote || (!known && !fileFailed && !devKey) ? null : showKey ? (
        <KeyEngraving
          eyebrow="Lock one open"
          title={keyTitle}
          prompt={keyMessage}
          buttonLabel={keyButton}
          onDone={() => {
            setEngraved(true);
            setDevKey(false);
          }}
        />
      ) : known &&
        COMPANION_ADVENTURES.includes(Number(adventureId)) &&
        missionNo >= 7 ? (
        <MissionGraduation />
      ) : sealing && !doorShown ? (
        <DoorView
          locksOpen={Math.min(missionNo, LOCK_WORDS.length)}
          onClose={() => setDoorShown(true)}
          autoCloseMs={3000}
        />
      ) : (
        <WellDone
          handler={handler}
          missionNo={missionNo}
          message={getOpt("WellDoneMessage")}
          button={getOpt("WellDoneButton") || "Thanks"}
          nextLock={sealing ? LOCK_WORDS[missionNo] : undefined}
          onGo={(choice) => {
            setNextMissionAt(typeof choice === "number" ? choice : null);
            if (choice === "now") {
              moveOn(choice);
              return;
            }
            // a text needs a number: on their first mission we have none yet;
            // then (or straight away) they confirm when it comes
            if (!hasPhone && !verified) setTextAsk(choice);
            else setConfirming(choice);
          }}
        />
      )}
      {/* when the next mission's text comes, to confirm: Confirm goes on to
          HQ (which books it), Change back to the choices */}
      {confirming !== null && sealing ? (
        <SealTomorrow
          name={operative}
          at={typeof confirming === "number" ? confirming : tomorrowMorning()}
          onSealed={(sealed) => {
            const choice = confirming;
            // HQ books this confirmed time, including the Tomorrow shortcut.
            setNextMissionAt(sealed.at);
            sealTomorrow(sealed);
            setConfirming(null);
            moveOn(choice);
          }}
          onChange={() => setConfirming(null)}
        />
      ) : confirming !== null ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="sm-next-mission"
          className="fixed inset-0 z-[70] grid animate-[smFadeIn_.2s_ease_both] place-items-center bg-black/70 p-4 font-sans"
        >
          <div className="w-full max-w-[360px] rounded-xl border border-[#d6a94e]/45 bg-[rgb(18_16_18/0.97)] px-[22px] pt-[20px] pb-[18px] text-center shadow-[0_20px_60px_rgba(0,0,0,.55)]">
            <div
              id="sm-next-mission"
              className="font-mono text-[10.5px] tracking-[.24em] text-[#d6a94e] uppercase"
            >
              ▸ Next mission
            </div>
            <p className="mt-3 text-[17px] leading-snug text-[#f4eddb]">
              {typeof confirming === "number"
                ? `You'll receive your next secret mission on ${new Date(
                    confirming,
                  ).toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })} around ${new Date(confirming).toLocaleTimeString(
                    undefined,
                    { hour: "numeric", minute: "2-digit" },
                  )}.`
                : "You'll receive your next mission text tomorrow at 8:00 AM Central Time."}
            </p>
            {bookingError && (
              <p role="alert" className="mt-3 text-sm text-red-300">
                {bookingError}
              </p>
            )}
            <button
              type="button"
              disabled={booking}
              onClick={async () => {
                const choice = confirming;
                setBooking(true);
                setBookingError("");
                try {
                  if (choice === "tomorrow" && SENDS_TEXTS) {
                    await apiClient.post(
                      API_ENDPOINTS.mission.nextDay(adventureId),
                      {
                        stepName: "Sign off",
                        tomorrow: true,
                      },
                    );
                  }
                  setConfirming(null);
                  moveOn(choice);
                } catch {
                  setBookingError(
                    "Couldn't schedule your text. Please try again.",
                  );
                } finally {
                  setBooking(false);
                }
              }}
              className={`mt-5 w-full ${ACTION_BTN_CLASS} rounded-lg px-4 py-3 text-[15px] font-semibold`}
            >
              {booking ? "Scheduling…" : "Confirm"}
            </button>
            <button
              type="button"
              disabled={booking}
              onClick={() => setConfirming(null)}
              className="mt-2 w-full cursor-pointer rounded-lg px-4 py-2.5 text-[14.5px] font-medium text-[#a99d86]"
            >
              Change
            </button>
          </div>
          <style>{`@keyframes smFadeIn{from{opacity:0}to{opacity:1}}`}</style>
        </div>
      ) : null}

      {/* Tomorrow / Schedule with no number on file: ask for it here, then
          confirm when the text comes */}
      <LoginModal
        open={textAsk !== null}
        title={
          typeof textAsk === "number"
            ? `Where should we text your mission on ${new Date(
                textAsk,
              ).toLocaleDateString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
              })} at ${new Date(textAsk).toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
              })}?`
            : "Where should we text tomorrow's secret mission?"
        }
        successMessage=""
        onClose={() => setTextAsk(null)}
        onSignedIn={() => {
          const choice = textAsk;
          setTextAsk(null);
          setVerified(true);
          void qc.invalidateQueries({ queryKey: ["secretmissions"] });
          if (choice !== null) setConfirming(choice);
        }}
      />
    </section>
  );
}
